import { useEffect, useState } from 'react';
import { Spinner } from '../../../components/spinner/spinner';
import { Field } from '../../../components/field/field';
import { Admin, PasswordRequirements } from '../../../clients/quipli-service/admin/admin';
import { toast } from '../../../util/toasts';
import { ApiError } from '../../../clients/axios-instance';
import { useAdmin } from '../../../hooks/use-admin-context';
import { Typography } from '../../../components/typography/typography';
import { useAdminApiV1 } from '../../../clients/quipli-service/admin/admin-api-v1';

const SPECIAL_CHARACTERS_REGEX = /[!@#$%^&*{};'[\]"]/g;
const SPECIAL_CHARACTERS = `!@#$%^&*{};'[]"`;

export const AdminUserForm = ({
  className,
  adminUser,
  close,
  setNewUser,
  type = 'create',
}: {
  className: string;
  adminUser?: Admin;
  close?: () => any;
  setNewUser?: (u: Admin) => void;
  type?: 'create' | 'setup' | 'changePassword';
}) => {
  const { createUserAdmin, updateUserAdmin, updateAdminLoggedProfile, getPasswordRequirements } = useAdminApiV1();

  const adminLogged = useAdmin();

  const [adminUsername, setUsername] = useState<string>('');
  const [adminFullName, setFullname] = useState<string>('');
  const [adminEmail, setEmail] = useState<string>('');
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>('');
  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirements>();
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  useEffect(() => {
    getPasswordRequirements()
      .then(setPasswordRequirements)
      .catch((e) => {
        console.log(e);
        toast('Error loading password requirements', 'error');
      });
  }, []);

  const clearValues = () => {
    setUsername(adminUser ? adminUser.adminUsername : '');
    setFullname(adminUser ? adminUser.adminFullName : '');
    setEmail(adminUser ? adminUser.adminEmail : '');
    close && close();
  };

  const onSubmit = async () => {
    if (isDisabled) return;

    if (!checkConfirmPassword) return;

    setIsLoading(true);
    await (adminUser
      ? adminUser.adminId === adminLogged.id
        ? updateAdminLoggedProfile({
            adminUsername,
            adminEmail,
            adminFullName,
            currentPassword: type == 'setup' ? undefined : currentPassword,
            adminPassword: type == 'setup' ? undefined : newPassword,
          })
        : updateUserAdmin(adminUser.adminId, {
            adminId: adminUser.adminId,
            adminUsername,
            adminEmail,
            adminFullName,
            adminPassword: type == 'setup' ? undefined : newPassword,
          })
      : createUserAdmin({ adminUsername, adminEmail, adminFullName, adminPassword: newPassword })
    )
      .then((admin: Admin) => {
        adminUser = admin;
        setNewUser && setNewUser(admin);
      })
      .catch((err: ApiError) => {
        const errMsg =
          err.status === 400 && err.message
            ? err.message
            : `Error ${type == 'create' ? 'creating' : 'updating'} admin user details`;
        toast(errMsg, 'error');
      })
      .finally(() => setIsLoading(false));

    onClose();
  };

  const onClose = () => {
    type == 'setup' || type == 'changePassword' ? clearValues() : close && close();
  };

  const checkConfirmPassword = () => {
    if (newPassword !== confirmNewPassword) {
      toast('Error password and confirm password should be the same', 'error');
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (adminUser) {
      setUsername(adminUser.adminUsername);
      setFullname(adminUser.adminFullName);
      setEmail(adminUser.adminEmail);
    }
  }, [adminUser]);

  useEffect(() => {
    if (passwordRequirements && newPassword) {
      if (type === 'changePassword' || type === 'create') {
        if (newPassword.length < passwordRequirements.minimumLength) {
          setErrorMessage(
            `Password must be at least ${passwordRequirements.minimumLength} character${
              passwordRequirements.minimumLength === 1 ? '' : 's'
            } long`,
          );
          return;
        }

        const lowercaseCharCount = newPassword.length - newPassword.replaceAll(/[a-z]/g, '').length;
        if (passwordRequirements.lowercaseCharacters > lowercaseCharCount) {
          setErrorMessage(
            `Password must contain at least ${passwordRequirements.lowercaseCharacters} lowercase letter${
              passwordRequirements.lowercaseCharacters === 1 ? '' : 's'
            }`,
          );
          return;
        }

        const uppercaseCharCount = newPassword.length - newPassword.replaceAll(/[A-Z]/g, '').length;
        if (passwordRequirements.uppercaseCharacters > uppercaseCharCount) {
          setErrorMessage(
            `Password must contain at least ${passwordRequirements.uppercaseCharacters} uppercase letter${
              passwordRequirements.uppercaseCharacters === 1 ? '' : 's'
            }`,
          );
          return;
        }

        const numericCharCount = newPassword.length - newPassword.replaceAll(/[0-9]/g, '').length;
        if (passwordRequirements.numericCharacters > numericCharCount) {
          setErrorMessage(
            `Password must contain at least ${passwordRequirements.numericCharacters} number${
              passwordRequirements.numericCharacters === 1 ? '' : 's'
            }`,
          );
          return;
        }

        const specialCharCount = newPassword.length - newPassword.replaceAll(SPECIAL_CHARACTERS_REGEX, '').length;
        if (passwordRequirements.specialCharacters > specialCharCount) {
          setErrorMessage(
            `Password must contain at least ${passwordRequirements.specialCharacters} of ${SPECIAL_CHARACTERS}`,
          );
          return;
        }

        if (newPassword !== confirmNewPassword) {
          setErrorMessage('Passwords do not match');
          return;
        }
        setErrorMessage(undefined);
      } else {
        setErrorMessage(undefined);
      }
    }
  }, [newPassword, confirmNewPassword, passwordRequirements]);

  useEffect(() => {
    if (!passwordRequirements || errorMessage) {
      setIsDisabled(true);
      return;
    }

    setIsDisabled(
      adminUsername.length === 0 ||
        adminFullName.length === 0 ||
        adminEmail.length === 0 ||
        (type === 'changePassword' && newPassword.length === 0) ||
        (adminUser?.adminId === adminLogged.id && currentPassword.length === 0),
    );
  }, [adminUsername, adminFullName, adminEmail, newPassword, currentPassword, errorMessage]);

  return (
    <div>
      <div className={className}>
        {(type == 'setup' || type == 'create') && (
          <div className="w-full">
            <Field
              className="w-full"
              type="text"
              label="Full name"
              value={adminFullName}
              onChange={(fullname: string) => {
                setFullname(fullname);
              }}
              isRequired={true}
            />
            <Field
              className="w-full"
              type="text"
              label="Primary email"
              value={adminEmail}
              onChange={(email: string) => {
                setEmail(email);
              }}
              isRequired={true}
            />
            <Field
              className="w-full"
              type="text"
              label="User name"
              value={adminUsername}
              onChange={(username: string) => {
                setUsername(username);
              }}
              isRequired={true}
            />
          </div>
        )}

        {(type == 'changePassword' || type == 'create') && (
          <div className="w-full flex flex-column gap-2">
            {type !== 'create' && adminUser?.adminId === adminLogged.id && (
              <Field
                data-password="check-password"
                className="w-full"
                type="password"
                label={'Current Password'}
                value={currentPassword}
                onChange={(password: string) => {
                  setCurrentPassword(password);
                }}
                isRequired={true}
              />
            )}
            <Field
              data-password="check-password"
              className="w-full"
              type="password"
              label={type === 'create' ? 'Password' : 'New Password'}
              value={newPassword}
              onChange={(password: string) => {
                setNewPassword(password);
              }}
              isRequired={true}
            />
            <Field
              className="w-full"
              type="password"
              label="Confirm Password"
              value={confirmNewPassword}
              onChange={(confirmPassword: string) => {
                setConfirmNewPassword(confirmPassword);
              }}
              isRequired={true}
            />
            {errorMessage && <div className="text-[var(--error-500)]">{errorMessage}</div>}
          </div>
        )}
      </div>

      <div className="dialog-actions !px-0 !pb-0">
        <div className={'flex flex-col sm:flex-row ' + (type === 'create' ? 'actionsContainer ' : 'flex-end')}>
          {isLoading ? (
            <Spinner />
          ) : (
            <>
              <button className="button button-block button-secondary mr-2 mb-2" onClick={onClose}>
                <Typography type="text-sm" weight="semi-bold" className="text-quipli-black-800">
                  Cancel
                </Typography>
              </button>

              <button className="button button-yellow button-primary mb-2" onClick={onSubmit} disabled={isDisabled}>
                <Typography type="text-sm" weight="semi-bold" className="text-quipli-black-800">
                  {adminUser ? 'Save changes' : 'Add'}
                </Typography>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
